import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, firstValueFrom, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { BusinessPartnerDto } from 'src/app/domain/models/dto/business-partner.dto';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { EPersonType } from 'src/app/domain/enums/person-type.enum';

@Component({
  selector: 'app-client-config',
  templateUrl: './client-config.component.html',
  styleUrls: ['./client-config.component.scss'],
})
export class ClientConfigComponent implements OnInit, OnDestroy {
  private readonly notifier: NotifierService;
  boobleModalData: any;
  public loading = false;
  configForm!: FormGroup;

  partners: BusinessPartnerDto[] = [];
  agencies: BusinessPartnerDto[] = [];
  agencyFilterCtrl = new FormControl('');
  filteredAgencies: ReplaySubject<BusinessPartnerDto[]> = new ReplaySubject(1);

  @ViewChild('agencySelect', { static: false }) agencySelect!: MatSelect;
  private _onDestroy = new Subject<void>();

  get config(): any {
    return this.configForm.value;
  }

  constructor(
    private router: Router,
    private modalService: BoobleModalService,
    notifierService: NotifierService,
    private formBuilder: FormBuilder,
    private readonly ordersService: OrdersService,
    private companyService: CorporateAccountService
  ) {
    this.notifier = notifierService;

    this.configForm = this.formBuilder.group({
      id: [null],
      isDefaultTariff: [null, Validators.required],
      isDefaultSpread: [null, Validators.required],
      businessPartnerId: ['', Validators.required],
      profileType: ['', Validators.required],
      personType: [null, Validators.required],
    });
  }

  async ngOnInit() {
    const partnersRes = await firstValueFrom(
      this.companyService
        .getPartnersList()
        .pipe(
          catchError(() =>
            of({ data: [] } as ResponseObjectModel<BusinessPartnerDto[]>)
          )
        )
    );

    this.partners = partnersRes.data ?? [];
    this.agencies = this.partners;
    this.filteredAgencies.next(this.agencies);

    this.agencyFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterAgencies());

    await this.loadClientDetails();
  }

  async loadClientDetails() {
    const initialData = this.boobleModalData ?? {};
    const clientId = initialData?.clientId;
    const personType = this.extractPersonType(initialData?.personType);

    if (!clientId || !personType) {
      this.notifier.notify(
        'error',
        'Dados insuficientes para carregar detalhes do cliente'
      );
      return;
    }

    try {
      const result = await firstValueFrom(
        this.companyService.getAccountStatusDetail(clientId, personType)
      );

      if (result.success && result.data) {
        const data = result.data;
        this.boobleModalData = data;

        this.configForm.patchValue({
          id: data.clientId,
          isDefaultTariff: data.isDefaultTariff ?? null,
          isDefaultSpread: data.isDefaultSpread ?? null,
          businessPartnerId: data.businessPartner?.id ?? '',
          profileType: data.profileType ?? '',
          personType: this.mapPersonTypeToEnum(data.personType),
        });

        if (data.isDefaultTariff === false) {
          this.configForm.addControl(
            'tariff',
            new FormControl(data.tariff, [
              Validators.required,
              Validators.pattern('.*\\S.*'),
            ])
          );
        }

        if (data.isDefaultSpread === false) {
          this.configForm.addControl(
            'spread',
            new FormControl(data.spread, [
              Validators.required,
              Validators.pattern('.*\\S.*'),
            ])
          );
        }
      } else {
        this.notifier.notify('error', 'Erro ao carregar detalhes do cliente');
      }
    } catch (error) {
      this.notifier.notify('error', 'Erro ao carregar detalhes do cliente');
    } finally {
      this.loading = false;
    }
  }

  private extractPersonType(personType: string): string {
    if (!personType) return '';

    if (personType.includes('Jurídica') || personType === 'PJ') return 'Pj';
    if (personType.includes('Física') || personType === 'PF') return 'Pf';

    return personType;
  }

  private filterAgencies() {
    const term = (this.agencyFilterCtrl.value || '').toString().toLowerCase();
    if (!term) {
      this.filteredAgencies.next(this.agencies);
      return;
    }
    this.filteredAgencies.next(
      this.agencies.filter((a) => {
        const name = (a.companyName || '').toLowerCase();
        const agency = String(a.agencyNumber ?? '');
        return name.includes(term) || agency.includes(term);
      })
    );
  }

  private mapPersonTypeToEnum(raw: unknown): EPersonType | null {
    if (raw === 'PJ - Pessoa Jurídica' || raw === 'PJ') return EPersonType.PJ;
    if (raw === 'PF - Pessoa Física' || raw === 'PF') return EPersonType.PF;

    if (raw === EPersonType.PF || raw === EPersonType.PJ)
      return raw as EPersonType;
    return null;
  }

  onSpreadChange(event: any) {
    const isDefaultSpreadValue = event.value === true;
    if (isDefaultSpreadValue) {
      if (this.configForm.contains('spread'))
        this.configForm.removeControl('spread');
    } else {
      if (!this.configForm.contains('spread')) {
        this.configForm.addControl(
          'spread',
          new FormControl(this.boobleModalData.spread || '', [
            Validators.required,
            Validators.pattern('.*\\S.*'),
          ])
        );
      }
    }
  }

  onTariffChange(event: any) {
    const isDefaultTariffValue = event.value === true;
    if (isDefaultTariffValue) {
      if (this.configForm.contains('tariff'))
        this.configForm.removeControl('tariff');
    } else {
      if (!this.configForm.contains('tariff')) {
        this.configForm.addControl(
          'tariff',
          new FormControl(this.boobleModalData.tariff || '', [
            Validators.required,
            Validators.pattern('.*\\S.*'),
          ])
        );
      }
    }
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  sendConfig() {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const payload = { ...this.config };

    this.companyService.setClientConfig(payload).subscribe({
      next: () => {
        this.closeModal();
        this.ordersService.updateOrderView();
        this.notifier.notify('success', 'Configuração salva com sucesso!');
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
